import { useTranslation } from "react-i18next";
import {
	type Column,
	HBarList,
	type HBarRow,
	HeatGrid,
	type HeatGridCell,
	type HolatRangi,
	holatVar,
} from "@/shared/charts";
import type { ReportAnalytics } from "../../../types";
import { hourWindow } from "../../../utils/series";
import { sonMatni } from "../../detail/KpiRow";

/**
 * Hodisa va dala layoutlarining umumiy bo'laklari (reja E3):
 * `SuspicionAnalytics`, `LeadsAnalytics`, `TasksAnalytics`.
 *
 * ⚠️⚠️ **`flagged` — `primary` NING QISMI** to'rt kind'ning hammasida
 * (yuqori darajali hodisa / oflayn lid / muddati o'tgan faol
 * topshiriq). Shuning uchun asosiy qatlam HAR JOYDA `total − flagged`
 * — ikkala qiymat ustma-ust qo'yilsa belgilanganlar ikki marta
 * sanalardi (E2 `ZoneExitsAnalytics` bilan bir xil qoida).
 */

export const TOP_N = 8;
export const SUNDAY = 7;

export function ikkiXona(soat: number): string {
	return String(soat).padStart(2, "0");
}

/**
 * Kategoriya kaliti → rang. Tartib bo'yicha holat ranglari aylanadi.
 *
 * ⚠️ Palitrada kategorik to'plam YO'Q (faqat 6 holat rangi) — 6 dan
 * ortiq kalitda rang takrorlanadi; farqni Donut legendasidagi nom va
 * qiymat beradi. `kelmagan` (qizil) oxirida — birinchi kalitlar
 * «xavf» ma'nosini olmasin.
 */
const KATEGORIYA_TARTIBI: readonly HolatRangi[] = [
	"kelgan",
	"kechikkan",
	"ertaKetgan",
	"uzrli",
	"neytral",
	"kelmagan",
];

export function kategoriyaRangi(indeks: number): string {
	return holatVar(KATEGORIYA_TARTIBI[indeks % KATEGORIYA_TARTIBI.length] as HolatRangi);
}

export interface Qatlam {
	/** `primary − flagged` qatlami nomi va rangi. */
	asosiy: { label: string; color: string };
	/** `flagged` qatlami nomi va rangi. */
	belgilangan: { label: string; color: string };
}

/**
 * Kunlik ustunlar: `[primary − flagged, flagged]`.
 * `tooltip(sana, asosiy, belgilangan)` — tayyor matn qaytaradi.
 */
export function kunlikUstunlar(
	daily: ReportAnalytics["daily"],
	qatlam: Qatlam,
	tooltip: (date: string, asosiy: string, belgilangan: string) => string
): Column[] {
	return daily.map((kun) => {
		const asosiy = Math.max(0, kun.primary - kun.flagged);
		return {
			key: kun.date,
			label: kun.date.slice(8, 10),
			topLabel: sonMatni(kun.primary),
			stack: [
				{ value: asosiy, color: qatlam.asosiy.color, label: qatlam.asosiy.label },
				{ value: kun.flagged, color: qatlam.belgilangan.color, label: qatlam.belgilangan.label },
			],
			tooltip: tooltip(kun.date, sonMatni(asosiy), sonMatni(kun.flagged)),
		};
	});
}

/**
 * `total`/`flagged` qatorlari → segmentli HBarList qatorlari.
 * `qiymatMatni(total, flagged)` — o'ng tomondagi yozuv.
 */
export function segmentliQatorlar<T extends { total: number; flagged: number }>(
	qatorlar: readonly T[],
	qatlam: Qatlam,
	kalit: (q: T) => string,
	nom: (q: T) => string,
	qiymatMatni: (total: string, flagged: string) => string
): HBarRow[] {
	return qatorlar.map((q) => ({
		key: kalit(q),
		label: nom(q),
		value: q.total,
		valueText:
			q.flagged > 0 ? qiymatMatni(sonMatni(q.total), sonMatni(q.flagged)) : sonMatni(q.total),
		segments: [
			{
				value: Math.max(0, q.total - q.flagged),
				color: qatlam.asosiy.color,
				label: qatlam.asosiy.label,
			},
			{ value: q.flagged, color: qatlam.belgilangan.color, label: qatlam.belgilangan.label },
		],
	}));
}

/** Bo'limlar kesimi — segmentli HBarList, bo'limsiz qator nomlanadi. */
export function BolimlarRoyxati({
	rows,
	qatlam,
	qiymatMatni,
}: {
	rows: ReportAnalytics["byDepartment"];
	qatlam: Qatlam;
	qiymatMatni: (total: string, flagged: string) => string;
}) {
	const { t } = useTranslation("reports");
	return (
		<HBarList
			empty={t("analytics.generic.empty")}
			rows={segmentliQatorlar(
				rows.slice(0, TOP_N),
				qatlam,
				(b) => b.departmentId ?? "__none",
				(b) => b.departmentName ?? t("analytics.generic.noDepartment"),
				qiymatMatni
			)}
		/>
	);
}

/**
 * Hafta kuni × soat issiqlik xaritasi (E2 qoidalari bilan bir xil):
 * soat oynasi ish vaqtidan ma'lumotga kengayadi (`hourWindow`),
 * yakshanba qatori faqat ma'lumot bo'lsa, cho'qqi izohda nomlanadi.
 */
export function HaftaSoatIssiqlik({ heat }: { heat: ReportAnalytics["heat"] }) {
	const { t } = useTranslation("reports");
	const bosh = t("analytics.generic.empty");
	const kunNomi = (kun: number) => t(`analytics.weekdayShort.${kun}`);

	if (heat.length === 0) {
		return (
			<div className="py-4 text-center text-xs" style={{ color: "var(--chart-axis)" }}>
				{bosh}
			</div>
		);
	}

	const oyna = hourWindow(heat.filter((k) => k.count > 0).map((k) => k.hour));
	const soatlar = Array.from({ length: oyna.to - oyna.from + 1 }, (_, i) => oyna.from + i);
	// ⚠️ Yakshanba faqat ma'lumot bo'lsa — bank ishlamaydigan kun qatori shovqin.
	const yakshanbaBor = heat.some((k) => k.weekday === SUNDAY && k.count > 0);
	const kunlar = [1, 2, 3, 4, 5, 6, ...(yakshanbaBor ? [SUNDAY] : [])];
	const kataklar: HeatGridCell[] = heat.map((k) => ({
		row: String(k.weekday),
		column: String(k.hour),
		value: k.count,
		tooltip: t("analytics.events.heatTooltip", {
			weekday: kunNomi(k.weekday),
			hour: ikkiXona(k.hour),
			count: sonMatni(k.count),
		}),
	}));
	const maks = Math.max(0, ...heat.map((k) => k.count));
	const choqqi = heat.find((k) => k.count === maks && maks > 0);

	return (
		<>
			<HeatGrid
				rows={kunlar.map((kun) => ({ key: String(kun), label: kunNomi(kun) }))}
				columns={soatlar.map((soat) => ({ key: String(soat), label: ikkiXona(soat) }))}
				cells={kataklar}
				showValues
				legendMinLabel="0"
				legendMaxLabel={sonMatni(maks)}
				empty={bosh}
			/>
			{choqqi ? (
				<div className="mt-2 text-xs" style={{ color: "var(--chart-axis)" }}>
					{t("analytics.events.heatPeak", {
						weekday: kunNomi(choqqi.weekday),
						hour: ikkiXona(choqqi.hour),
					})}
				</div>
			) : null}
		</>
	);
}

/**
 * Shkala yuqori chegarasi — 1/2/5 × 10ⁿ ga yumaloqlanadi, `max/2`
 * butun bo'ladi (E2 dagi bilan bir xil qoida).
 */
export function chiroyliMax(qiymat: number): number {
	if (!(qiymat > 0)) {
		return 1;
	}

	const daraja = 10 ** Math.floor(Math.log10(qiymat));
	const qadam = [1, 2, 5, 10].find((k) => k * daraja >= qiymat) ?? 10;
	const natija = qadam * daraja;
	return natija >= 2 && natija % 2 === 0 ? natija : natija * 2;
}
